import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const packages = [
  {
    id: 'launch',
    number: '01',
    name: 'LAUNCH',
    tag: null,
    best: 'New businesses or those starting fresh',
    price: 'From $1,500',
    priceNote: 'AUD',
    features: [
      { icon: 'ri-layout-4-line', text: 'Custom Website (up to 5 pages)' },
      { icon: 'ri-smartphone-line', text: 'Mobile Optimised & Speed Tuned' },
      { icon: 'ri-google-line', text: 'Google Business Profile Setup' },
      { icon: 'ri-search-2-line', text: 'Basic On-Page SEO' },
      { icon: 'ri-customer-service-2-line', text: '1 Month Support Included' },
    ],
    cta: 'Get Started',
    highlight: false,
  },
  {
    id: 'dominate',
    number: '02',
    name: 'DOMINATE',
    tag: '★ Most Popular',
    best: 'Established businesses ready to grow',
    price: 'From $3,000',
    priceNote: 'AUD',
    features: [
      { icon: 'ri-check-double-line', text: 'Everything in Launch' },
      { icon: 'ri-brain-line', text: 'AI Chat & Lead Response Setup' },
      { icon: 'ri-map-pin-2-line', text: 'Local SEO Campaign (3 months)' },
      {
        icon: 'ri-robot-2-line',
        text: 'GEO / AEO Foundation Setup',
        tooltip: 'Includes schema markup, AI citation profile setup, and basic answer-engine optimisation so AI tools start recognising your business. The Full Power package upgrades this to an ongoing GEO/AEO campaign with content strategy and continuous AI optimisation.',
        learnMore: '/geo-aeo',
      },
      { icon: 'ri-bar-chart-grouped-line', text: 'Competitor Analysis Report' },
      { icon: 'ri-line-chart-line', text: 'Monthly Reporting & Strategy Call' },
    ],
    cta: 'Get Started',
    highlight: true,
  },
  {
    id: 'fullpower',
    number: '03',
    name: 'DARKSIDE FULL POWER',
    tag: null,
    best: 'Businesses serious about market domination',
    price: 'Custom Pricing',
    priceNote: '',
    features: [
      { icon: 'ri-check-double-line', text: 'Everything in Dominate' },
      { icon: 'ri-cpu-line', text: 'Full AI Integration & Automation' },
      { icon: 'ri-quill-pen-line', text: 'AI Content Generation System' },
      { icon: 'ri-trophy-line', text: 'Ongoing SEO & Reputation Management' },
      { icon: 'ri-sparkling-2-line', text: 'Full GEO / AEO Campaign', link: '/geo-aeo' },
      { icon: 'ri-radar-line', text: 'Ongoing Competitor Monitoring' },
      { icon: 'ri-calendar-check-line', text: 'Quarterly Strategy Reviews' },
      { icon: 'ri-shield-star-line', text: 'Priority Support' },
    ],
    cta: 'Get Started',
    highlight: false,
  },
];

const compareCategories = [
  {
    label: 'Foundation',
    rows: [
      { feature: 'Custom Website', launch: '5 pages', dominate: '5+ pages', power: 'Custom scope' },
      { feature: 'Mobile Optimised & Speed Tuned', launch: true, dominate: true, power: true },
      { feature: 'Google Business Profile Setup', launch: true, dominate: true, power: true },
      { feature: 'On-Page SEO', launch: 'Basic', dominate: 'Advanced', power: 'Advanced' },
      { feature: 'Support Included', launch: '1 Month', dominate: '3 Months', power: 'Priority Ongoing' },
    ],
  },
  {
    label: 'Local & Search',
    rows: [
      { feature: 'Local SEO Campaign', launch: false, dominate: '3 Months', power: 'Ongoing' },
      { feature: 'Competitor Analysis', launch: false, dominate: 'One-off Report', power: 'Ongoing Monitoring' },
      { feature: 'Reputation Management', launch: false, dominate: false, power: true },
    ],
  },
  {
    label: 'AI & GEO / AEO',
    rows: [
      { feature: 'AI Chat & Lead Response', launch: false, dominate: true, power: true },
      { feature: 'GEO / AEO Optimisation', launch: false, dominate: 'Foundation Setup', power: 'Full Campaign' },
      { feature: 'AI Content Generation', launch: false, dominate: false, power: true },
      { feature: 'Full AI Integration & Automation', launch: false, dominate: false, power: true },
    ],
  },
  {
    label: 'Reporting & Strategy',
    rows: [
      { feature: 'Monthly Reporting & Strategy Call', launch: false, dominate: true, power: true },
      { feature: 'Quarterly Strategy Reviews', launch: false, dominate: false, power: true },
    ],
  },
];

type CellValue = string | boolean;

function CompareCell({ value, highlight }: { value: CellValue; highlight?: boolean }) {
  if (value === true) {
    return (
      <span className={`inline-flex items-center justify-center w-6 h-6 ${highlight ? 'text-ds-red' : 'text-white/60'}`}>
        <i className="ri-check-line text-base" />
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 text-white/15">
        <i className="ri-subtract-line text-base" />
      </span>
    );
  }
  return (
    <span className={`font-body text-xs leading-snug ${highlight ? 'text-white' : 'text-white/50'}`}>
      {value}
    </span>
  );
}

export default function PricingSection() {
  const groupRef = useScrollRevealGroup();
  const [hovered, setHovered] = useState<string | null>(null);
  const [tableOpen, setTableOpen] = useState(false);

  return (
    <section id="pricing" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      {/* Grid background */}
      <div className="absolute inset-0 hero-grid opacity-15 pointer-events-none" />
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-ds-red/5 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">Investment</span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-none reveal">
              SIMPLE.
              <br />
              <span className="text-ds-red neon-text-red">TRANSPARENT.</span>
              <br />
              NO SURPRISES.
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs reveal">
              Every business is different, so we tailor every engagement. But here&apos;s how most clients get started.
            </p>
          </div>
          {/* Accent line */}
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(255,23,68,0.5)' }} />
        </div>

        {/* Packages grid */}
        <div className="grid md:grid-cols-3 gap-6 reveal">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              onMouseEnter={() => setHovered(pkg.id)}
              onMouseLeave={() => setHovered(null)}
              className={`
                relative flex flex-col transition-all duration-300 cursor-default
                ${pkg.highlight
                  ? 'border border-ds-red bg-ds-bg-2'
                  : 'border border-ds-border bg-ds-bg-2 hover:border-white/20'
                }
                ${hovered === pkg.id && !pkg.highlight ? 'border-white/20' : ''}
              `}
              style={pkg.highlight ? { boxShadow: '0 0 40px rgba(180,255,57,0.12), inset 0 0 40px rgba(180,255,57,0.03)' } : {}}
            >
              {/* Most popular badge */}
              {pkg.tag && (
                <div className="absolute -top-px left-0 right-0 flex justify-center">
                  <span className="bg-ds-red text-white font-display text-xs tracking-widest px-4 py-1">
                    {pkg.tag}
                  </span>
                </div>
              )}

              <div className={`p-8 flex flex-col flex-1 ${pkg.tag ? 'pt-10' : ''}`}>
                {/* Package number + name */}
                <div className="mb-6">
                  <span className="font-display text-ds-red text-xs tracking-[0.3em]">
                    PACKAGE {pkg.number}
                  </span>
                  <h3 className="font-display text-2xl text-white mt-1 leading-tight tracking-wider">
                    {pkg.name}
                  </h3>
                  <p className="font-body text-white/40 text-xs mt-2 leading-relaxed">
                    Best for: {pkg.best}
                  </p>
                </div>

                {/* Divider */}
                <div className={`h-px mb-6 ${pkg.highlight ? 'bg-ds-red/40' : 'bg-ds-border'}`} />

                {/* Features */}
                <ul className="space-y-3 flex-1 mb-8">
                  {pkg.features.map((f) => (
                    <li key={f.text} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${f.icon} ${pkg.highlight ? 'text-ds-red' : 'text-white/40'} text-sm`} />
                      </div>
                      <div className="flex flex-col gap-1 min-w-0">
                        <span className="font-body text-sm leading-snug flex items-center gap-1.5">
                          {f.link ? (
                            <Link
                              to={f.link}
                              className="text-white/70 hover:text-ds-red transition-colors duration-150 inline-flex items-center gap-1 group/link cursor-pointer"
                            >
                              {f.text}
                              <i className="ri-arrow-right-up-line text-xs text-white/30 group-hover/link:text-ds-red transition-colors duration-150" />
                            </Link>
                          ) : (
                            <span className="text-white/70">{f.text}</span>
                          )}
                          {f.tooltip && (
                            <span className="relative group/tip inline-flex items-center flex-shrink-0">
                              <i className="ri-information-line text-white/30 group-hover/tip:text-ds-red text-xs cursor-help transition-colors duration-150" />
                              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-[#0f0f0f] border border-ds-border text-white/70 font-body text-xs leading-relaxed px-3 py-2.5 opacity-0 group-hover/tip:opacity-100 transition-opacity duration-200 z-50 shadow-lg whitespace-normal text-left">
                                {f.tooltip}
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ds-border" />
                              </span>
                            </span>
                          )}
                        </span>
                        {f.learnMore && (
                          <Link
                            to={f.learnMore}
                            className="font-body text-xs text-ds-red/60 hover:text-ds-red transition-colors duration-150 inline-flex items-center gap-1 cursor-pointer w-fit"
                          >
                            Learn more about GEO / AEO
                            <i className="ri-arrow-right-line text-[10px]" />
                          </Link>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className={`font-display text-3xl ${pkg.highlight ? 'text-white' : 'text-white/90'}`}>
                      {pkg.price}
                    </span>
                    {pkg.priceNote && (
                      <span className="font-body text-white/40 text-xs">{pkg.priceNote}</span>
                    )}
                  </div>
                  {!pkg.priceNote && (
                    <p className="font-body text-white/40 text-xs mt-1">Tailored to your requirements</p>
                  )}
                </div>

                {/* CTA */}
                <a
                  href="#contact"
                  className={`
                    whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 font-display text-xs tracking-widest transition-all duration-200
                    ${pkg.highlight
                      ? 'bg-ds-red text-white hover:bg-ds-red/90 border border-ds-red'
                      : 'bg-transparent text-white border border-ds-border hover:border-white/40'
                    }
                  `}
                  style={pkg.highlight ? { boxShadow: '0 0 20px rgba(180,255,57,0.3)' } : {}}
                >
                  {pkg.cta}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Fine print */}
        <p className="font-body text-white/30 text-xs text-center mt-6 reveal">
          All prices are in AUD and exclude GST. Custom quotes available for larger projects or multi-location businesses.
        </p>

        {/* ── Compare Packages Table ── */}
        <div className="mt-14 reveal">
          {/* Toggle header */}
          <button
            type="button"
            onClick={() => setTableOpen((v) => !v)}
            className="w-full flex items-center justify-between px-6 py-4 border border-ds-border bg-ds-bg-2 hover:border-white/20 transition-colors duration-200 cursor-pointer group"
          >
            <div className="flex items-center gap-3">
              <i className="ri-table-2 text-ds-red text-sm" />
              <span className="font-display text-sm tracking-[0.2em] text-white/80 group-hover:text-white transition-colors duration-200">
                COMPARE PACKAGES
              </span>
              <span className="font-body text-white/30 text-xs">— see every feature side by side</span>
            </div>
            <i className={`ri-arrow-down-s-line text-white/40 group-hover:text-white/70 text-lg transition-all duration-300 ${tableOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Table body */}
          {tableOpen && (
            <div className="border border-t-0 border-ds-border bg-ds-bg-2 overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse">
                {/* Column headers */}
                <thead>
                  <tr>
                    <th className="text-left px-6 py-5 w-[44%]">
                      <span className="font-body text-white/30 text-xs tracking-widest uppercase">Feature</span>
                    </th>
                    {[
                      { id: 'launch', label: 'LAUNCH', sub: 'From $1,500', highlight: false },
                      { id: 'dominate', label: 'DOMINATE', sub: 'From $3,000', highlight: true },
                      { id: 'power', label: 'FULL POWER', sub: 'Custom', highlight: false },
                    ].map((col) => (
                      <th
                        key={col.id}
                        className={`text-center px-4 py-5 w-[18.6%] ${col.highlight ? 'bg-ds-red/5' : ''}`}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <span className={`font-display text-xs tracking-[0.2em] ${col.highlight ? 'text-ds-red' : 'text-white/70'}`}>
                            {col.label}
                          </span>
                          <span className="font-body text-white/30 text-[11px]">{col.sub} AUD</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compareCategories.map((cat, catIdx) => (
                    <>
                      {/* Category label row */}
                      <tr key={`cat-${catIdx}`} className="border-t border-ds-border">
                        <td colSpan={4} className="px-6 py-2.5 bg-white/[0.02]">
                          <span className="font-display text-[10px] tracking-[0.3em] text-ds-red/70 uppercase">
                            {cat.label}
                          </span>
                        </td>
                      </tr>
                      {/* Feature rows */}
                      {cat.rows.map((row, rowIdx) => (
                        <tr
                          key={`${catIdx}-${rowIdx}`}
                          className="border-t border-ds-border/50 hover:bg-white/[0.015] transition-colors duration-150"
                        >
                          <td className="px-6 py-3.5">
                            <span className="font-body text-sm text-white/55">{row.feature}</span>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <CompareCell value={row.launch} />
                          </td>
                          <td className="px-4 py-3.5 text-center bg-ds-red/5">
                            <CompareCell value={row.dominate} highlight />
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <CompareCell value={row.power} />
                          </td>
                        </tr>
                      ))}
                    </>
                  ))}
                  {/* Pricing footer row */}
                  <tr className="border-t border-ds-border">
                    <td className="px-6 py-5">
                      <span className="font-display text-xs tracking-widest text-white/60 uppercase">Starting Investment</span>
                    </td>
                    {[
                      { label: 'From $1,500', note: 'AUD', highlight: false, cta: 'Get Started' },
                      { label: 'From $3,000', note: 'AUD', highlight: true, cta: 'Get Started' },
                      { label: 'Custom', note: 'Tailored quote', highlight: false, cta: 'Get Started' },
                    ].map((col, i) => (
                      <td key={i} className={`px-4 py-5 text-center ${col.highlight ? 'bg-ds-red/5' : ''}`}>
                        <div className="flex flex-col items-center gap-2.5">
                          <div>
                            <div className={`font-display text-base ${col.highlight ? 'text-white' : 'text-white/70'}`}>
                              {col.label}
                            </div>
                            <div className="font-body text-white/30 text-[11px] mt-0.5">{col.note}</div>
                          </div>
                          <a
                            href="#contact"
                            className={`
                              whitespace-nowrap inline-flex items-center justify-center gap-1.5 px-4 py-2 font-display text-[10px] tracking-widest transition-all duration-200
                              ${col.highlight
                                ? 'bg-ds-red text-white border border-ds-red hover:bg-ds-red/90'
                                : 'bg-transparent text-white/60 border border-ds-border hover:border-white/30 hover:text-white'
                              }
                            `}
                          >
                            {col.cta}
                            <i className="ri-arrow-right-line text-[10px]" />
                          </a>
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-16 border border-ds-border bg-ds-bg-2 p-6 lg:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 reveal">
          <div>
            <h3 className="font-display text-3xl text-white mb-2">NOT SURE WHERE TO START?</h3>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xl">
              Book a free 30-minute strategy call. We&apos;ll look at your current online presence, show you what your competitors are doing, and recommend the best path forward — no pressure, no obligation.
            </p>
            <p className="font-body text-ds-red text-xs mt-3 tracking-wider">
              Takes 30 minutes. Could change everything.
            </p>
          </div>
          <div className="flex-shrink-0">
            <a
              href="#contact"
              className="btn-red whitespace-nowrap"
            >
              <span>Get Your Free AI Visibility Audit</span>
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
